import React, { useState, useEffect } from 'react';
import Modal from '../../../../components/shareable/modal';
import { useAuth } from '../../../../context/authContext';
import { formatPhoneNumber } from '../../../../utils/formatPhoneNumber';
import AdditionalLocations from './AdditionalLocations';

const initialCustomerData = {
  customer: '',
  address: '',
  address2: '',
  city: '',
  state: '',
  zip: '',
  standard_discount: '',
  primary_contact: '',
  title: '',
  phone: '',
  email: '',
  notes: '',
  additional_locations: []
};

const CreateCustomerModal = ({ isOpen, onClose, onSave, Update = "Create Customer" }) => {
  const [formData, setFormData] = useState(initialCustomerData);
  const [uploading, setUploading] = useState(false);
  const [errors, setErrors] = useState({});
  const [locationErrors, setLocationErrors] = useState([]);
  const { user } = useAuth();

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setFormData(initialCustomerData);
      setErrors({});
      setLocationErrors([]);
    }
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Special handling for phone field
    if (name === 'phone') {
      // Remove all non-digit characters
      const cleanedValue = value.replace(/\D/g, '');
      // Limit to 10 digits
      const limitedValue = cleanedValue.slice(0, 10);
      setFormData((prev) => ({ ...prev, [name]: limitedValue }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Handle changes for additional locations
  const handleLocationsChange = (updatedLocations) => {
    setFormData(prev => ({ ...prev, additional_locations: updatedLocations }));
  };

  // Handle errors for additional locations
  const handleLocationErrorsChange = (updatedErrors) => { setLocationErrors(updatedErrors); };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.customer.trim()) { newErrors.customer = 'Customer name is required'; }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    
    // Validate all locations have a location name
    const newLocationErrors = [...locationErrors];
    let hasLocationError = false;
    
    formData.additional_locations.forEach((location, index) => {
      if (!location.location.trim()) {
        newLocationErrors[index] = true;
        hasLocationError = true;
      } else {
        newLocationErrors[index] = false;
      }
    });
    
    if (hasLocationError) { setLocationErrors(newLocationErrors); }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0 && !hasLocationError;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) { return; }
    
    setUploading(true);
    
    try {
      const dataToSave = {
        ...formData,
        primary_contact: formData.primary_contact || null,
        notes: formData.notes ? [
          {
            user: user?.email, // Use authenticated user's email directly
            text: formData.notes,
            date: new Date()
          }
        ] : [],
        additional_locations: formData.additional_locations
      };
      
      await onSave(dataToSave);
      onClose(); // Form will be reset by useEffect when isOpen changes
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setUploading(false);
    }
  };

  // Define the order of fields to match your schema
  const orderedFields = [
    'customer', 'primary_contact', 'email',
    'phone', 'address', 'address2',
    'city', 'state', 'zip',
    'title', 'standard_discount', 'notes'
  ];

  // Group fields into rows of 3
  const fieldGroups = [];
  for (let i = 0; i < orderedFields.length; i += 3) {
    fieldGroups.push(orderedFields.slice(i, i + 3));
  }

  return (
    <Modal isOpen={isOpen} width='3xl' onClose={onClose}>
      <div className="flex flex-col h-full">
        <h2 className="text-lg font-semibold mb-4">{Update}</h2>
        
        {/* Scrollable form content */}
        <div className="flex-grow overflow-y-auto max-h-96">
          <form onSubmit={handleSubmit} className="space-y-4">
            {fieldGroups.map((group, groupIndex) => (
              <div key={groupIndex} className="grid grid-cols-3 gap-4">
                {group.map((field) => (
                  <div key={field} className={field === 'notes' ? 'col-span-3' : ''}>
                    <label className="block text-sm font-medium mb-1 capitalize">
                      {field.replace(/_/g, ' ')}
                      {(field === 'customer' || field === 'email') && <span className="text-red-500">*</span>}
                    </label>
                    {field === 'notes' ? (
                      <textarea
                        name={field}
                        value={formData[field]}
                        onChange={handleChange}
                        className={`w-full p-2 border rounded hover:border-red-300 focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500 transition-colors ${errors[field] ? 'border-red-500' : ''}`}
                        placeholder={`Enter ${field.replace(/_/g, ' ')}`}
                        rows={2}
                      />
                    ) : field === 'primary_contact' ? (
                      <input
                        type="text"
                        name={field}
                        value={formData[field]}
                        onChange={handleChange}
                        className={`w-full p-2 border rounded hover:border-red-300 focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500 transition-colors ${errors[field] ? 'border-red-500' : ''}`}
                        placeholder={`Enter ${field.replace(/_/g, ' ')}`}
                      />
                    ) : field === 'phone' ? (
                      <input
                        type="text"
                        name={field}
                        value={formatPhoneNumber(formData[field])} // Format for display
                        onChange={handleChange}
                        className={`w-full p-2 border rounded hover:border-red-300 focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500 transition-colors ${errors[field] ? 'border-red-500' : ''}`}
                        placeholder="(123) 456-7890"
                      />
                    ) : (
                      <input
                        type={field === 'email' ? 'email' : 'text'}
                        name={field}
                        value={formData[field]}
                        onChange={handleChange}
                        className={`w-full p-2 border rounded hover:border-red-300 focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500 transition-colors ${errors[field] ? 'border-red-500' : ''}`}
                        placeholder={`Enter ${field.replace(/_/g, ' ')}`}
                        required={field === 'customer' || field === 'email'}
                      />
                    )}
                    {errors[field] && <p className="mt-1 text-xs text-red-500">{errors[field]}</p>}
                  </div>
                ))}
              </div>
            ))}
            
            {/* Additional Locations Section */}
            <AdditionalLocations locations={formData.additional_locations} onLocationsChange={handleLocationsChange} locationErrors={locationErrors} onLocationErrorsChange={handleLocationErrorsChange} />
          </form>
        </div>
        
        {/* Fixed button area at the bottom */}
        <div className="flex justify-end space-x-2 mt-4 pt-4 border-t">
          <button type="button" onClick={onClose} className="px-6 py-1.5 bg-gray-300 rounded hover:bg-gray-400 text-sm" disabled={uploading} >
            Cancel
          </button>
          <button type="submit" onClick={handleSubmit} className="px-6 py-1.5 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 text-sm" disabled={uploading} >
            {uploading ? 'Creating...' : 'Create'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default CreateCustomerModal;
